import { useState, useEffect, useRef } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Send, Trash2, X, AtSign, Loader2, Check, CheckCheck } from "lucide-react";

export function CommentsPanel({ 
  isOpen, 
  onClose, 
  orderId, 
  orderName,
  currentUserEmail,
  isAdmin,
  onNavigateToOrder,
  onNotificationUpdate
}) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentions, setSelectedMentions] = useState([]);
  const textareaRef = useRef(null);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && orderId) {
      loadComments();
      loadUsers();
    }
  }, [isOpen, orderId]);

  useEffect(() => {
    // Scroll to bottom when comments change
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const loadComments = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API}/orders/${orderId}/comments`, {
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error("Failed to load comments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await authFetch(`${API}/users/list`, {
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setNewComment(value);

    // Detect @ mention
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      setMentionFilter(atMatch[1].toLowerCase());
      setShowMentionList(true);
      
      // Position the mention dropdown
      const textarea = textareaRef.current;
      if (textarea) {
        const rect = textarea.getBoundingClientRect();
        setMentionPosition({
          top: rect.top - 150,
          left: rect.left
        });
      }
    } else {
      setShowMentionList(false);
    }
  };

  const insertMention = (user) => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = newComment.substring(0, cursorPos);
    const textAfterCursor = newComment.substring(cursorPos);
    
    // Find the @ symbol position
    const atPos = textBeforeCursor.lastIndexOf("@");
    const newText = textBeforeCursor.substring(0, atPos) + `@${user.name} ` + textAfterCursor;
    
    setNewComment(newText);
    setShowMentionList(false);
    
    // Add to selected mentions if not already there
    if (!selectedMentions.includes(user.email)) {
      setSelectedMentions([...selectedMentions, user.email]);
    }
    
    // Focus back on textarea
    textareaRef.current?.focus();
  };

  const handleSubmit = async () => {
    if (!newComment.trim()) return;

    setIsSending(true);
    try {
      // Extract mentions from the comment text
      const mentionRegex = /@(\w+(?:\s\w+)*)/g;
      const mentionedNames = [];
      let match;
      while ((match = mentionRegex.exec(newComment)) !== null) {
        mentionedNames.push(match[1].toLowerCase());
      }

      // Find user emails for mentioned names
      const mentions = users
        .filter(u => mentionedNames.some(name => u.name.toLowerCase().includes(name)))
        .map(u => u.email);

      const response = await authFetch(`${API}/orders/${orderId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: newComment,
          mentions: [...new Set([...mentions, ...selectedMentions])]
        })
      });

      if (response.ok) {
        setNewComment("");
        setSelectedMentions([]);
        loadComments();
        toast.success("Commentaire ajouté");
      } else {
        toast.error("Erreur lors de l'ajout du commentaire");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout du commentaire");
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm("Supprimer ce commentaire ?")) return;

    try {
      const response = await authFetch(`${API}/comments/${commentId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setComments(comments.filter(c => c.comment_id !== commentId));
        toast.success("Commentaire supprimé");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const markAsRead = async (commentId) => {
    try {
      const response = await authFetch(`${API}/orders/${orderId}/comments/${commentId}/read`, {
        method: "PUT",
      });
      if (response.ok) {
        setComments(prev => prev.map(c => c.comment_id === commentId ? { ...c, is_read: true } : c));
        if (onNotificationUpdate) onNotificationUpdate();
      }
    } catch { /* silent */ }
  };

  const markAllAsRead = async () => {
    try {
      const response = await authFetch(`${API}/orders/${orderId}/comments/read-all`, {
        method: "PUT",
      });
      if (response.ok) {
        setComments(prev => prev.map(c => ({ ...c, is_read: true })));
        toast.success("Tous les commentaires marqués comme lus");
        if (onNotificationUpdate) onNotificationUpdate();
      }
    } catch { /* silent */ }
  };

  const unreadComments = comments.filter(c => !c.is_read);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(mentionFilter) ||
    u.email.toLowerCase().includes(mentionFilter)
  );

  const handleCommentClick = () => {
    if (onNavigateToOrder && orderId) {
      onClose();
      onNavigateToOrder(orderId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">Commentaires</h3>
              <p className="text-sm text-zinc-500">{orderName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadComments.length > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                data-testid="mark-all-read-btn"
                title="Tout marquer comme lu"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tout lu
              </button>
            )}
            <button 
              onClick={onClose} 
              className="p-1 hover:bg-zinc-200 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucun commentaire</p>
              <p className="text-sm">Soyez le premier à commenter !</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div 
                key={comment.comment_id} 
                onClick={handleCommentClick}
                className={`p-3 rounded-lg cursor-pointer transition-all hover:ring-1 hover:ring-blue-300 hover:shadow-sm ${
                  comment.is_read
                    ? "bg-zinc-50 opacity-60"
                    : comment.user_email === currentUserEmail 
                      ? "bg-blue-50 ml-8" 
                      : "bg-zinc-100 mr-8"
                }`}
                title="Cliquer pour accéder à la ligne"
                data-testid="comment-navigate"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-zinc-900">
                        {comment.user_name}
                      </span>
                      <span className="text-xs text-zinc-400">
                        {formatDate(comment.created_at)}
                      </span>
                      {comment.is_read && (
                        <span className="text-xs text-green-500 flex items-center gap-0.5">
                          <Check className="w-3 h-3" /> Lu
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-700 whitespace-pre-wrap">
                      {comment.content.split(/(@\w+(?:\s\w+)*)/).map((part, i) => 
                        part.startsWith("@") ? (
                          <span key={i} className="text-blue-600 font-medium">{part}</span>
                        ) : (
                          <span key={i}>{part}</span>
                        )
                      )}
                    </p>
                    {comment.mentions?.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-zinc-500">
                        <AtSign className="w-3 h-3" />
                        {comment.mentions.length} mention{comment.mentions.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {!comment.is_read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(comment.comment_id); }}
                        className="p-1 hover:bg-green-100 rounded text-zinc-400 hover:text-green-600 transition-colors"
                        title="Marquer comme lu"
                        data-testid="mark-read-btn"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {(comment.user_email === currentUserEmail || isAdmin) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(comment.comment_id); }}
                        className="p-1 hover:bg-red-100 rounded text-zinc-400 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={commentsEndRef} />
        </div>

        {/* New Comment Input */}
        <div className="p-4 border-t border-zinc-200 bg-zinc-50 rounded-b-xl relative">
          {/* Mention Autocomplete */}
          {showMentionList && filteredUsers.length > 0 && (
            <div 
              className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-lg shadow-lg border border-zinc-200 max-h-40 overflow-y-auto z-10"
            >
              {filteredUsers.slice(0, 5).map((user) => (
                <button
                  key={user.email}
                  onClick={() => insertMention(user)}
                  className="w-full px-3 py-2 text-left hover:bg-zinc-100 flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{user.name}</p>
                    <p className="text-xs text-zinc-500">{user.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                value={newComment}
                onChange={handleTextChange}
                placeholder="Écrivez un commentaire... Utilisez @ pour mentionner quelqu'un"
                className="resize-none min-h-[80px]"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.ctrlKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <p className="text-xs text-zinc-400 mt-1">
                Ctrl+Entrée pour envoyer
              </p>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={!newComment.trim() || isSending}
              className="self-end"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
